import './BrandingFields.scss';

import { useMemo, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashCan } from '@fortawesome/free-solid-svg-icons';

import Button from 'components/base/Button';
import { Info } from 'components/base/Info';
import { BRANDING_ACCEPT_ATTRIBUTE, readBrandingImageValue } from 'helpers/brandingImage';

import { BrandingImageModel } from './BrandingImageModel';
import { useBrandingImageBridge } from './useBrandingImageBridge';

/**
 * Editor for the `branding` config key: upload an image, choose how a
 * non-square one is fitted, and see the square that will actually reach the
 * top-bar.
 *
 * A pure `observer` — every decision lives on {@link BrandingImageModel}. The
 * component is bridged into the surrounding modal, which is still `useState`
 * driven: `prepareSave` only ever sees `formData`, so a reaction pushes the
 * model's value into it rather than the modal reading the model.
 */
const BrandingFields: React.FC<{
  formData: unknown;
  setFormData: React.Dispatch<React.SetStateAction<any>>;
  setJsonValid: (valid: boolean) => void;
}> = observer(({ formData, setFormData, setJsonValid }) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Seeded once from the row being edited. Every later write to formData
  // originates from this model, so re-seeding on formData changes would feed
  // the model its own output.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const model = useMemo(() => new BrandingImageModel(readBrandingImageValue(formData)), []);

  useBrandingImageBridge(model, setFormData, setJsonValid);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) void model.selectFile(file);
  };

  const handleRemove = () => {
    model.clear();
    // Without this, re-picking the same file fires no change event.
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <>
      {/* Offset instead of an empty label column, so the callout lines up with the fields rather than spanning the dialog. */}
      <Row className="mb-3">
        <Col sm={{ span: 10, offset: 2 }}>
          <Info heading="Shown in the top-right corner of the top-bar, on every screen.">
            Upload anything that makes this installation recognisable at a glance — a company logo, an environment name such as Test or Acceptance, a colour. It
            is stored as a small square, and everyone signed in here sees the same one.
          </Info>
        </Col>
      </Row>

      <Form.Group as={Row} className="mb-3" controlId="brandingImageFile">
        <Form.Label column sm="2">
          Image
        </Form.Label>
        <Col sm="10">
          <div className="d-flex align-items-start gap-2">
            <Form.Control ref={fileInputRef} type="file" accept={BRANDING_ACCEPT_ATTRIBUTE} onChange={handleFileChange} isInvalid={!model.isValid} />
            <Button
              variant="phoenix-danger"
              onClick={handleRemove}
              disabled={model.dataUrl === null}
              startIcon={<FontAwesomeIcon icon={faTrashCan} />}
              className="flex-shrink-0"
            >
              Remove
            </Button>
          </div>
          {model.errorMessage !== null ? (
            <Form.Text className="text-danger">{model.errorMessage}</Form.Text>
          ) : (
            <Form.Text className="text-muted">PNG, JPEG, GIF, WebP or SVG.</Form.Text>
          )}
        </Col>
      </Form.Group>

      <Form.Group as={Row} className="mb-3" controlId="brandingImageFit">
        <Form.Label column sm="2">
          Shape
        </Form.Label>
        <Col sm="10">
          <Form.Check
            type="radio"
            id="brandingImageFitContain"
            name="brandingImageFit"
            label="Fit whole image — nothing is cut off"
            checked={model.fit === 'contain'}
            disabled={!model.canChooseFit}
            onChange={() => model.setFit('contain')}
          />
          <Form.Check
            type="radio"
            id="brandingImageFitCover"
            name="brandingImageFit"
            label="Fill the square — the edges are cropped"
            checked={model.fit === 'cover'}
            disabled={!model.canChooseFit}
            onChange={() => model.setFit('cover')}
          />
          {!model.canChooseFit ? <Form.Text className="text-muted">Choose an image to set its shape.</Form.Text> : null}
        </Col>
      </Form.Group>

      <Form.Group as={Row} className="mb-3" controlId="brandingImagePreview">
        <Form.Label column sm="2">
          Preview
        </Form.Label>
        <Col sm="10">
          <div className="branding-preview">
            <div className="branding-preview__frame">
              {model.dataUrl !== null ? <img className="branding-image" src={model.dataUrl} alt="Branding" /> : null}
            </div>
            <span className="text-muted">
              {model.dataUrl === null ? 'No image — the top-bar stays as it is' : `Stores as ${Math.max(1, Math.round(model.byteLength / 1024))} KB`}
            </span>
          </div>
        </Col>
      </Form.Group>
    </>
  );
});

export default BrandingFields;
